<template>
  <div class="pi-ds-wrap" :aria-busy="loading ? 'true' : 'false'">
    <div v-if="loading" class="pi-ds pi-ds--loading" role="status" aria-live="polite">
      <span class="pi-ds__skeleton" aria-hidden="true"></span>
      <span class="pi-ds__skeleton pi-ds__skeleton--mid" aria-hidden="true"></span>
      <span class="pi-ds__skeleton pi-ds__skeleton--short" aria-hidden="true"></span>
      <span class="pi-ds__text">{{ loadingText }}</span>
    </div>

    <div v-else-if="error" class="pi-ds" :class="'pi-ds--' + error.kind" role="alert">
      <p class="pi-ds__title">{{ errorTitle }}</p>
      <p class="pi-ds__text">{{ error.message }}</p>
      <button v-if="error.retryable" type="button" class="pi-btn" @click="$emit('retry')">Tentar de novo</button>
    </div>

    <div v-else-if="unavailableText" class="pi-ds pi-ds--na" role="status">
      <p class="pi-ds__title">n/d</p>
      <p class="pi-ds__text">{{ unavailableText }}</p>
      <ul v-if="unavailableNotes.length" class="pi-ds__notes">
        <li v-for="(note, index) in unavailableNotes" :key="index">{{ note }}</li>
      </ul>
    </div>

    <div v-else-if="empty" class="pi-ds pi-ds--empty" role="status">
      <p class="pi-ds__title">Sem dados</p>
      <p class="pi-ds__text">{{ emptyLabel }}</p>
      <ul v-if="emptyNotes.length" class="pi-ds__notes">
        <li v-for="(note, index) in emptyNotes" :key="index">{{ note }}</li>
      </ul>
    </div>

    <div v-else class="pi-ds-content">
      <p v-if="status.partial" class="pi-ds__banner" role="status">{{ status.partialText }}</p>
      <ul v-if="status.notes.length" class="pi-ds__notes">
        <li v-for="(note, index) in status.notes" :key="index">{{ note }}</li>
      </ul>
      <slot/>
    </div>

    <p v-if="footerVisible" class="pi-ds__footer">
      <span v-if="status.asOfText">{{ status.asOfText }}</span>
      <span v-if="status.definitionVersion">Definição v{{ status.definitionVersion }}</span>
      <span v-if="coverageText">{{ coverageText }}</span>
      <router-link v-if="catalogLink" class="pi-link" to="/product-intelligence/status">Catálogo e status da coleta</router-link>
    </p>
  </div>
</template>

<script>
import { emptyText, envelopeStatus, notesWithout, unavailableReason } from "./piState.js";
import { EMPTY, formatDateFull } from "./piFormat.js";

const ERROR_TITLES = {
  session: "Sessão da API v2 ausente",
  disabled: "Indisponível neste ambiente",
  forbidden: "Sem permissão",
  invalid: "Filtro inválido",
  failed: "Erro ao carregar",
};

export default {
  name: "pi-data-state",
  props: {
    loading: { type: Boolean, default: false },
    error: { type: Object, default: null },
    empty: { type: Boolean, default: false },
    envelope: { type: Object, default: null },
    emptyMessage: { type: String, default: "" },
    naReason: { type: String, default: "" },
    loadingText: { type: String, default: "Carregando…" },
    showFooter: { type: Boolean, default: true },
    catalogLink: { type: Boolean, default: true },
  },
  computed: {
    status() {
      return envelopeStatus(this.envelope);
    },
    errorTitle() {
      const kind = this.error && this.error.kind;
      return ERROR_TITLES[kind] || ERROR_TITLES.failed;
    },
    emptyLabel() {
      return this.emptyMessage || emptyText(this.envelope);
    },
    unavailableText() {
      return this.naReason || unavailableReason(this.envelope, this.empty);
    },
    unavailableNotes() {
      return this.naReason ? [] : notesWithout(this.envelope, this.unavailableText);
    },
    emptyNotes() {
      return notesWithout(this.envelope, this.emptyLabel);
    },
    coverageText() {
      const parts = [];
      const started = formatDateFull(this.status.collectionStartedAt);
      const backfilled = formatDateFull(this.status.backfilledFrom);
      if (started !== EMPTY) parts.push("Coleta desde " + started);
      if (backfilled !== EMPTY) parts.push("retroativo derivado desde " + backfilled);
      return parts.join(", ");
    },
    footerVisible() {
      return this.showFooter && !this.loading && !this.error && !!this.envelope;
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-ds {
  margin: 8px 0 20px;
  padding: 20px;
  border: 1px solid #e6e7eb;
  border-radius: 6px;
  background: #fafbfc;
}

.pi-ds__title {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 700;
  color: #3a424e;
}

.pi-ds__text {
  margin: 0 0 10px;
  font-size: 13px;
  color: #4e5560;
}

.pi-ds--forbidden,
.pi-ds--session,
.pi-ds--failed {
  border-color: #f3c2c9;
  background: #fff7f8;
}

.pi-ds--disabled,
.pi-ds--invalid {
  border-color: #f2d9a6;
  background: #fffbf2;
}

.pi-ds--loading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pi-ds__skeleton {
  display: block;
  height: 14px;
  width: 100%;
  border-radius: 4px;
  background: #eceef1;
}

.pi-ds__skeleton--mid {
  width: 70%;
}

.pi-ds__skeleton--short {
  width: 40%;
}

.pi-ds__banner {
  margin: 0 0 12px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fff4dc;
  color: #7a4f00;
  font-size: 12px;
  font-weight: 600;
}

.pi-ds__notes {
  margin: 0 0 12px;
  padding-left: 18px;
  font-size: 12px;
  color: #4e5560;
}

.pi-ds__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 16px 0 0;
  padding-top: 10px;
  border-top: 1px solid #eef0f2;
  font-size: 11px;
  color: #6b7280;
}

.pi-ds__footer .pi-link {
  font-size: 11px;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piDataState.vue